'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { AnimatePresence, motion } from 'framer-motion'
import { useContext, useMemo } from 'react'
import type { ObjectShape } from 'yup'
import BookingCalendar from './BookingCalendar'
import BookingEvent from './BookingEvent'
import { BookingEventPaymentShell } from './BookingEventPaymentShell'
import CategorySelection from './CategorySelection'
import { BookingContext, BookingContextProvider, type BookingHooks } from './context'
import EventAlreadyBookedDialog from './EventAlreadyBookedDialog'
import Payment from './Payment'
import type { EventCategory, EventLocationSelection, ParsedBookableEvent, Therapist } from './types'

type BookingProps = BookingHooks & {
    therapist: Therapist
    layout?: 'small' | 'large'
    requireCaptcha?: boolean
    requireTermsConsent?: boolean
    showQuickFilters?: boolean
    showLocationQuickFilter?: boolean
    showTherapistProfile?: boolean
    profileUrl?: string
    categorySelectionHeading?: string
    buildFailureComponent: (backToCalendar: () => void) => React.ReactNode
    buildSuccessComponent: (
        event: ParsedBookableEvent,
        selectedLocation: EventLocationSelection,
        category: EventCategory,
        therapist: Therapist
    ) => React.ReactNode
    buildLoginComponent?: (onDone: () => void, onBack: () => void) => React.ReactNode
    additionalBookingFieldsBefore?: React.ReactNode
    additionalConfirmationCheckboxes?: Record<string, React.ReactNode>
    additionalInitialValues?: Record<string, any>
    additionalBookingValidationSchemaFields?: ObjectShape
    bookingNote?: string | null
    previewMode?: boolean
    fixedCategoryId?: number
    skipInitialCategorySelection?: boolean
    isClientAuthenticated?: boolean
    onClickAlreadyHaveAccount?: () => void
    checkoutRestoreScopeKey?: string
}

export default function Booking(props: BookingProps) {
    return (
        <BookingContextProvider {...props}>
            <BookingSteps
                buildFailureComponent={props.buildFailureComponent}
                buildSuccessComponent={props.buildSuccessComponent}
                buildLoginComponent={props.buildLoginComponent}
            />
        </BookingContextProvider>
    )
}

type BookingStepsProps = {
    buildFailureComponent: (backToCalendar: () => void) => React.ReactNode
    buildSuccessComponent: (
        event: ParsedBookableEvent,
        selectedLocation: EventLocationSelection,
        category: EventCategory,
        therapist: Therapist
    ) => React.ReactNode
    buildLoginComponent?: (onDone: () => void, onBack: () => void) => React.ReactNode
}

function BookingSteps({ buildFailureComponent, buildSuccessComponent, buildLoginComponent }: BookingStepsProps) {
    const { state, bookingEvent, navigationDirection, setState, setNavigationDirection } = useContext(BookingContext)

    const variants = useMemo(
        () => ({
            initial: (direction: string) => ({
                x: direction === 'back' ? '-100%' : '100%',
                opacity: 0
            }),
            animate: {
                x: 0,
                opacity: 1,
                transition: { duration: 0.3 }
            },
            exit: (direction: string) => ({
                x: direction === 'back' ? '100%' : '-100%',
                opacity: 0,
                transition: { duration: 0.3 }
            })
        }),
        []
    )

    return (
        <div className="h-full w-full flex flex-col min-h-0 overflow-hidden">
            <AnimatePresence custom={navigationDirection} mode="wait">
                {state === 'category-selection' && (
                    <motion.div
                        key="category-selection"
                        className="w-full h-full max-h-152 lg:max-h-none py-4 flex flex-col"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        <CategorySelection />
                    </motion.div>
                )}
                {state === 'calendar' && (
                    <motion.div
                        key="calendar"
                        className="w-full flex flex-col flex-1 min-h-0 pb-0 lg:overflow-hidden"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        <BookingCalendar />
                    </motion.div>
                )}
                {bookingEvent && (state === 'booking' || state === 'payment') && (
                    <motion.div
                        key="booking-flow"
                        className="w-full h-full py-4 overflow-y-auto"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        <BookingEventPaymentShell>
                            <AnimatePresence custom={navigationDirection} mode="wait">
                                {state === 'booking' && (
                                    <motion.div
                                        key="booking-content"
                                        className="w-full"
                                        custom={navigationDirection}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        variants={variants}
                                    >
                                        <BookingEvent />
                                    </motion.div>
                                )}
                                {state === 'payment' && (
                                    <motion.div
                                        key="payment-content"
                                        className="w-full"
                                        custom={navigationDirection}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                        variants={variants}
                                    >
                                        <Payment />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </BookingEventPaymentShell>
                    </motion.div>
                )}
                {state === 'login' && buildLoginComponent && (
                    <motion.div
                        key="login"
                        data-testid="booking-login"
                        className="w-full h-full py-4 overflow-y-auto"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        {buildLoginComponent(
                            () => {
                                setNavigationDirection('back')
                                setState('booking')
                            },
                            () => {
                                setNavigationDirection('back')
                                setState('booking')
                            }
                        )}
                    </motion.div>
                )}
                {state === 'success' && (
                    <motion.div
                        key="success"
                        data-testid="booking-success"
                        className="w-full h-full py-4 overflow-y-auto"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        <BookingSuccess buildSuccessComponent={buildSuccessComponent} />
                    </motion.div>
                )}
                {state === 'failure' && (
                    <motion.div
                        key="failure"
                        data-testid="booking-failure"
                        className="w-full h-full py-4 overflow-y-auto"
                        custom={navigationDirection}
                        initial="initial"
                        animate="animate"
                        exit="exit"
                        variants={variants}
                    >
                        <BookingFailure buildFailureComponent={buildFailureComponent} />
                    </motion.div>
                )}
            </AnimatePresence>
            <EventAlreadyBookedDialog />
        </div>
    )
}

interface BookingSuccessProps {
    buildSuccessComponent: (
        event: ParsedBookableEvent,
        selectedLocation: EventLocationSelection,
        category: EventCategory,
        therapist: Therapist
    ) => React.ReactNode
}

function BookingSuccess({ buildSuccessComponent }: BookingSuccessProps) {
    const { therapist, bookingEvent, bookingResult, categories } = useContext(BookingContext)
    const { t } = useComponentTranslation()

    if (!bookingEvent || !bookingResult) {
        return <ComponentSpinner />
    }

    const category = categories[bookingEvent.categoryId]
    const paymentPending = bookingResult.paymentStatus === 'pending'

    return (
        <div className="flex flex-col gap-4">
            {paymentPending ? (
                <div
                    className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
                    data-testid="booking-payment-pending"
                >
                    {t('booking.payment.pendingConfirmation')}
                </div>
            ) : null}
            {buildSuccessComponent(bookingEvent, bookingResult.locationSelection, category, therapist)}
        </div>
    )
}

interface BookingFailureProps {
    buildFailureComponent: (backToCalendar: () => void) => React.ReactNode
}

function BookingFailure({ buildFailureComponent }: BookingFailureProps) {
    const { setState } = useContext(BookingContext)
    return buildFailureComponent(() => setState('calendar'))
}
